import { PassengerAuthenticationState } from '@backoffice-frontend/graphql/subscription'
import { useEffect, useEffectEvent, useState } from 'react'

export const PENDING_TIMEOUT_MS = 35000

/**
 * States in which the backend has finished processing the passenger's
 * authentication, so a manual check-in or no-show can no longer change it.
 */
const TERMINAL_STATES: PassengerAuthenticationState[] = [
  PassengerAuthenticationState.Confirmed,
  PassengerAuthenticationState.Cancelled,
]

export const isTerminalAuthenticationState = (
  state: PassengerAuthenticationState | undefined,
): boolean => !!state && TERMINAL_STATES.includes(state)

export const useManualActionPending = ({
  authenticationState,
  onTimeout,
}: {
  authenticationState: PassengerAuthenticationState | undefined
  onTimeout: () => void
}) => {
  const [isActionRequested, setIsActionRequested] = useState(false)

  // The timer must only restart when `isPending` changes. An effect event keeps the
  // callback out of the effect's dependencies, so a caller passing a new function on
  // every render cannot reset the 35-second countdown.
  const handleTimeout = useEffectEvent(() => {
    setIsActionRequested(false)
    onTimeout()
  })

  const isPending =
    isActionRequested && !isTerminalAuthenticationState(authenticationState)

  useEffect(() => {
    if (!isPending) {
      setIsActionRequested(false)
      return
    }

    const timeoutId = setTimeout(handleTimeout, PENDING_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [isPending])

  return {
    isPending,
    startPending: () => setIsActionRequested(true),
  }
}
